/**
 * Responsive book cover images.
 *
 * Uses vite-imagetools at build time to generate AVIF + WebP variants at
 * 240 / 480 / 720 px widths from every PNG in src/assets/books.
 * Renders a <picture> with <source srcset sizes> so browsers pick the
 * smallest acceptable format and width for the device.
 *
 * Non-lazy for above-the-fold candidates (featured book), lazy elsewhere.
 *
 * MATCHING (fixed):
 *   Callers pass `src` = the plain imported cover URL (e.g. site.ts does
 *   `import cover from "@/assets/books/ai-is-not-your-enemy.png"`). That is the
 *   SAME hashed URL that a `?url` import yields, so we map imported-URL ->
 *   responsive descriptor directly. The previous version re-derived the source
 *   filename from the hashed URL with a greedy regex that collapsed every
 *   hyphenated name to its first word (ai-is-not-your-enemy-HASH.png -> ai.png),
 *   so the lookup missed for ALL covers and the component silently fell back to
 *   the full-size original PNG. That is what shipped ~15 MB with zero next-gen
 *   images to /books.
 */

import { useState } from "react";

type PictureDescriptor = {
  img: { src: string; w: number; h: number };
  sources: Record<string, string>;
};

// Responsive AVIF/WebP/PNG variants for every cover, keyed by glob path.
const pictureModules = import.meta.glob(
  "/src/assets/books/*.png",
  {
    query: "?w=240;480;720&format=avif;webp;png&as=picture",
    import: "default",
    eager: true,
  },
) as Record<string, PictureDescriptor>;

// The plain Vite-hashed URL of each original cover, keyed by the SAME glob path.
// This is exactly what a plain `import cover from ".../x.png"` produces, which
// is what `book.image` / `promo.image` hold.
const urlModules = import.meta.glob(
  "/src/assets/books/*.png",
  { query: "?url", import: "default", eager: true },
) as Record<string, string>;

// Direct map: imported cover URL -> responsive descriptor. No hash parsing.
const byOriginalUrl: Record<string, PictureDescriptor> = {};
for (const [globPath, url] of Object.entries(urlModules)) {
  const pic = pictureModules[globPath];
  if (pic) byOriginalUrl[url] = pic;
}

/** Resolve the responsive picture descriptor for an imported cover URL. */
function pictureFor(src: string): PictureDescriptor | undefined {
  const direct = byOriginalUrl[src];
  if (direct) return direct;

  // Defensive fallback: strip a single trailing "-<8charhash>" (Vite default,
  // no internal hyphens) and try to match by original filename. This only runs
  // if the direct URL map ever misses; it never over-matches hyphenated names.
  const base = src.split("/").pop() ?? "";
  const original = base.replace(/-[A-Za-z0-9_]{8}(\.[a-z0-9]+)$/i, "$1");
  for (const [globPath, pic] of Object.entries(pictureModules)) {
    if (globPath.endsWith(`/${original}`)) return pic;
  }
  return undefined;
}

/** Public accessor so callers (e.g. the LCP preloader) can grab the responsive picture data for an imported book cover. */
export function getBookPicture(src: string) {
  return pictureFor(src);
}

interface Props {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
}

export const BookCoverImage = ({ src, alt, className, sizes, priority }: Props) => {
  const pic = pictureFor(src);
  const [loaded, setLoaded] = useState(false);
  const imgClass = [
    className,
    "transition-opacity duration-500 ease-out",
    loaded ? "opacity-100" : "opacity-0",
  ]
    .filter(Boolean)
    .join(" ");
  const commonImg = {
    alt,
    onLoad: () => setLoaded(true),
    className: imgClass,
    decoding: "async" as const,
    loading: priority ? ("eager" as const) : ("lazy" as const),
    ...(priority ? { fetchPriority: "high" as const } : {}),
  };
  const sizesAttr =
    sizes ?? "(min-width: 1024px) 22vw, (min-width: 640px) 45vw, 90vw";

  // Blur-up placeholder: a subtle skeleton pulse behind the image until it loads.
  const placeholder = (
    <span
      aria-hidden="true"
      className={[
        "absolute inset-0 bg-gradient-to-br from-navy-elevated to-navy-deep",
        "transition-opacity duration-500 ease-out",
        loaded ? "opacity-0" : "opacity-100 animate-pulse",
      ].join(" ")}
    />
  );

  if (!pic) {
    return (
      <span className="relative block h-full w-full overflow-hidden">
        {placeholder}
        <img src={src} width={480} height={600} sizes={sizesAttr} {...commonImg} />
      </span>
    );
  }

  return (
    <span className="relative block h-full w-full overflow-hidden">
      {placeholder}
      <picture>
        {pic.sources.avif && (
          <source type="image/avif" srcSet={pic.sources.avif} sizes={sizesAttr} />
        )}
        {pic.sources.webp && (
          <source type="image/webp" srcSet={pic.sources.webp} sizes={sizesAttr} />
        )}
        <img
          src={pic.img.src}
          width={pic.img.w}
          height={pic.img.h}
          sizes={sizesAttr}
          {...commonImg}
        />
      </picture>
    </span>
  );
};
